import { GenericCallCard, GenericEventCard } from './chunk-2C555TJ2.js';
import { SubCard } from './chunk-6XCO5YFI.js';
import { ProtocolBadge } from './chunk-H6QQORH4.js';
import { ONEINCH, ONEINCH_COMPONENTS, ONEINCH_EVENTS, ONEINCH_CALLS, ONEINCH_KINDS, isRecord, ONEINCH_FIELDS, ProtocolValue, humanizeFieldName, FactGrid, decodedOrRaw, fieldAtPath, legField, SectionCard, ProtocolDisclosure, ProtocolParamList } from './chunk-E4NIZZY2.js';
import { rawOf, looksLikeAddress, isEnriched, kindOf, displayOf } from '@authe/fe-commons/storage';
import { RawToggle } from '@authe/ui-core';
import { jsxs, jsx } from 'react/jsx-runtime';
import { ArrowRight } from 'lucide-react';

function OneInchAddress({
  decoded,
  display,
  raw,
  chain,
  renderAddress
}) {
  const struct = isRecord(decoded) ? decoded : void 0;
  const candidate = rawOf(struct?.[ONEINCH_FIELDS.address] ?? display ?? raw);
  const address = typeof candidate === "string" ? candidate : void 0;
  if (address && looksLikeAddress(address) && renderAddress) {
    return /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 flex-wrap items-center gap-1", children: [
      renderAddress(address, { chain }),
      /* @__PURE__ */ jsx(RawToggle, { raw })
    ] });
  }
  return /* @__PURE__ */ jsxs("span", { children: [
    /* @__PURE__ */ jsx(ProtocolValue, { value: address ?? display ?? raw }),
    /* @__PURE__ */ jsx(RawToggle, { raw })
  ] });
}
var VENUE_CHIP = "inline-flex items-center rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-2xs font-medium";
function OneInchPool({
  decoded,
  display,
  raw,
  chain,
  renderAddress
}) {
  const hop = isRecord(decoded) ? decoded : void 0;
  if (!hop) return /* @__PURE__ */ jsx(ProtocolValue, { value: display ?? raw });
  const pool = rawOf(hop[ONEINCH_FIELDS.address]);
  const venue = hop[ONEINCH_FIELDS.poolProtocol];
  const modernDirection = rawOf(hop[ONEINCH_FIELDS.zeroForOne]);
  const legacyDirection = rawOf(hop.oneForZero);
  const zeroForOne = typeof modernDirection === "boolean" ? modernDirection : typeof legacyDirection === "boolean" ? !legacyDirection : void 0;
  const poolNode = typeof pool === "string" && looksLikeAddress(pool) && renderAddress ? renderAddress(pool, { chain }) : /* @__PURE__ */ jsx(ProtocolValue, { value: pool });
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 flex-wrap items-center gap-1.5 align-middle", children: [
    venue != null && /* @__PURE__ */ jsx("span", { className: VENUE_CHIP, title: "AMM family, from the descriptor's protocol bits", children: /* @__PURE__ */ jsx(ProtocolValue, { value: venue }) }),
    poolNode,
    typeof zeroForOne === "boolean" && /* @__PURE__ */ jsxs(
      "span",
      {
        className: "inline-flex items-center gap-0.5 text-2xs text-muted-foreground",
        title: zeroForOne ? "Trading token0 into token1" : "Trading token1 into token0",
        children: [
          zeroForOne ? "token0" : "token1",
          /* @__PURE__ */ jsx(ArrowRight, { className: "size-3 shrink-0", "aria-hidden": true }),
          zeroForOne ? "token1" : "token0"
        ]
      }
    ),
    Object.entries(hop).filter(([key]) => !["address", "poolProtocol", "zeroForOne", "oneForZero"].includes(key)).map(([key, value2]) => /* @__PURE__ */ jsxs("span", { className: "text-2xs text-muted-foreground", children: [
      key,
      ": ",
      /* @__PURE__ */ jsx(ProtocolValue, { value: value2 })
    ] }, key)),
    /* @__PURE__ */ jsx(RawToggle, { raw })
  ] });
}
var CHIP = "inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-2xs";
function OneInchTraits({
  decoded,
  display,
  raw,
  hint
}) {
  const traits = isRecord(decoded) ? decoded : void 0;
  if (!traits) return /* @__PURE__ */ jsx(ProtocolValue, { value: display ?? raw });
  const named = Array.isArray(hint?.fields) ? hint.fields.filter((n) => typeof n === "string") : [];
  const order = [
    ...new Set(named.filter((n) => n in traits)),
    ...Object.keys(traits).filter((n) => !named.includes(n))
  ];
  const chips = order.filter((name) => rawOf(traits[name]) !== false && rawOf(traits[name]) != null).map((name) => /* @__PURE__ */ jsxs("span", { className: CHIP, title: name === "allowedSenderSuffix" ? "Only the low 80 bits of the allowed sender; not a complete address. Zero permits any sender." : humanizeFieldName(name), children: [
    /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: name === "allowedSenderSuffix" ? "Allowed sender suffix (80 bits)" : humanizeFieldName(name) }),
    rawOf(traits[name]) !== true && /* @__PURE__ */ jsx("span", { className: "font-medium", children: /* @__PURE__ */ jsx(ProtocolValue, { value: traits[name] }) })
  ] }, name));
  if (chips.length === 0) return /* @__PURE__ */ jsxs("span", { children: [
    /* @__PURE__ */ jsx(ProtocolValue, { value: display ?? raw }),
    /* @__PURE__ */ jsx(RawToggle, { raw })
  ] });
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex flex-wrap items-center gap-1 align-middle", children: [
    chips,
    /* @__PURE__ */ jsx(RawToggle, { raw })
  ] });
}
var FIELDS = {
  "oneinch.fill_args": ["target", "extension", "interaction", "trailingData"],
  "oneinch.extension": ["makerAssetSuffix", "takerAssetSuffix", "makingAmountData", "takingAmountData", "predicate", "makerPermit", "preInteractionData", "postInteractionData", "customData"],
  "oneinch.permit": ["token", "owner", "spender", "value", "deadline", "permit"]
};
var TITLES = {
  "oneinch.fill_args": "Fill arguments",
  "oneinch.extension": "Order extension",
  "oneinch.permit": "Permit payload"
};
function OneInchPayload({ kind, decoded, raw, display, chain, confidence }) {
  const fields = isRecord(decoded) ? decoded : void 0;
  if (!fields) return /* @__PURE__ */ jsxs("span", { children: [
    /* @__PURE__ */ jsx(ProtocolValue, { value: display ?? raw }),
    /* @__PURE__ */ jsx(RawToggle, { raw })
  ] });
  const names = [.../* @__PURE__ */ new Set([...FIELDS[kind] ?? [], ...Object.keys(fields)])].filter((k) => fields[k] != null);
  return /* @__PURE__ */ jsxs(SubCard, { caption: TITLES[kind] ?? "1inch payload", children: [
    typeof confidence === "number" && confidence >= 0 && confidence <= 1 && /* @__PURE__ */ jsxs("p", { className: "text-2xs text-muted-foreground", children: [
      "Interpretation ",
      Math.round(confidence * 100),
      "%"
    ] }),
    /* @__PURE__ */ jsx(FactGrid, { facts: names.map((name) => {
      const value2 = fields[name];
      const inner = decodedOrRaw(value2);
      const targeted = isRecord(inner) && Object.hasOwn(inner, "target") && Object.hasOwn(inner, "data");
      let content;
      if (targeted && !isEnriched(value2)) {
        content = /* @__PURE__ */ jsxs("div", { className: "min-w-0 space-y-1", children: [
          /* @__PURE__ */ jsx(ProtocolValue, { value: inner.target, chain, type: "address" }),
          /* @__PURE__ */ jsxs("details", { className: "min-w-0", children: [
            /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs", children: "Payload" }),
            /* @__PURE__ */ jsx(ProtocolValue, { value: inner.data, chain }),
            Object.entries(inner).filter(([k]) => k !== "target" && k !== "data").map(([k, v]) => /* @__PURE__ */ jsxs("div", { children: [
              humanizeFieldName(k),
              ": ",
              /* @__PURE__ */ jsx(ProtocolValue, { value: v, chain })
            ] }, k))
          ] })
        ] });
      } else {
        content = /* @__PURE__ */ jsx(ProtocolValue, { value: value2, chain });
      }
      return { label: humanizeFieldName(name), value: content };
    }) }),
    /* @__PURE__ */ jsx(RawToggle, { raw })
  ] });
}
function OneInchOrderCall(props) {
  const { call: call2, chain } = props;
  const order = fieldAtPath(call2.params, "order");
  if (!order || legField(order, "makerAsset") == null || legField(order, "takerAsset") == null) return /* @__PURE__ */ jsx(GenericCallCard, { ...props });
  const fact2 = (label, value2) => ({ label, when: value2 != null, value: /* @__PURE__ */ jsx(ProtocolValue, { value: value2, chain }) });
  const args = fieldAtPath(call2.params, "args");
  const extension = fieldAtPath(call2.params, "extension");
  return /* @__PURE__ */ jsx(GenericCallCard, { ...props, children: /* @__PURE__ */ jsxs("div", { className: "space-y-3 min-w-0", "data-testid": "oneinch-order", children: [
    /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Signed order terms \xB7 amounts in base units" }),
    /* @__PURE__ */ jsx(FactGrid, { facts: [
      fact2("Maker", legField(order, "maker")),
      { label: "Maker offers", value: /* @__PURE__ */ jsxs("span", { className: "inline-flex flex-wrap gap-2", children: [
        /* @__PURE__ */ jsx(ProtocolValue, { value: legField(order, "makingAmount") }),
        /* @__PURE__ */ jsx(ProtocolValue, { value: legField(order, "makerAsset"), chain })
      ] }) },
      { label: "Maker requests", value: /* @__PURE__ */ jsxs("span", { className: "inline-flex flex-wrap gap-2", children: [
        /* @__PURE__ */ jsx(ProtocolValue, { value: legField(order, "takingAmount") }),
        /* @__PURE__ */ jsx(ProtocolValue, { value: legField(order, "takerAsset"), chain })
      ] }) },
      fact2("Maker traits", legField(order, "makerTraits")),
      fact2("Requested fill amount", fieldAtPath(call2.params, "amount")),
      fact2("Taker traits", fieldAtPath(call2.params, "takerTraits")),
      fact2("Taker", fieldAtPath(call2.params, "taker"))
    ] }),
    [[args, "oneinch.fill_args"], [extension, "oneinch.extension"]].map(([value2, kind]) => kindOf(value2) === kind && typeof kind === "string" ? /* @__PURE__ */ jsx(ProtocolValue, { value: value2, chain }, kind) : null)
  ] }) });
}
var PID = "oneinch_fusion_plus";
var ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
var fact = (label, value2) => ({
  label,
  when: value2 != null,
  value: /* @__PURE__ */ jsx(ProtocolValue, { value: value2 })
});
function EscrowTerms({ value: value2 }) {
  const token = legField(value2, "token");
  return /* @__PURE__ */ jsx(FactGrid, { facts: [
    fact("Order hash", legField(value2, "orderHash")),
    fact("Hashlock", legField(value2, "hashlock")),
    fact("Maker", legField(value2, "maker")),
    fact("Taker", legField(value2, "taker")),
    { label: "Escrow asset", when: token != null, value: displayOf(token) === ZERO_ADDRESS ? /* @__PURE__ */ jsxs("span", { children: [
      "Native asset ",
      /* @__PURE__ */ jsx(ProtocolValue, { value: token })
    ] }) : /* @__PURE__ */ jsx(ProtocolValue, { value: token }) },
    fact("Escrow amount (base units)", legField(value2, "amount")),
    fact("Safety deposit (native base units)", legField(value2, "safetyDeposit")),
    fact("Timelocks", legField(value2, "timelocks")),
    fact("Fee parameters", legField(value2, "parameters"))
  ] });
}
function OneInchEscrowCall(props) {
  const terms = fieldAtPath(props.call.params, "dstImmutables", "immutables");
  if (terms == null) return /* @__PURE__ */ jsx(GenericCallCard, { ...props });
  const creating = props.call.label === "createDstEscrow";
  return /* @__PURE__ */ jsx(GenericCallCard, { ...props, children: /* @__PURE__ */ jsxs("div", { className: "space-y-3 min-w-0", "data-testid": "oneinch-escrow-call", children: [
    /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: creating ? "Destination escrow request. Locking an escrow does not establish delivery; check its withdrawal and the linked journey." : "Requested escrow operation. Check the execution outcome and transfers for the amounts actually released or refunded." }),
    /* @__PURE__ */ jsx(EscrowTerms, { value: terms }),
    /* @__PURE__ */ jsx(FactGrid, { facts: [
      fact("Withdrawal target", fieldAtPath(props.call.params, "target")),
      fact("Source cancellation timestamp", fieldAtPath(props.call.params, "srcCancellationTimestamp"))
    ] })
  ] }) });
}
function OneInchEscrowEvent({ event: event2, hint }) {
  const terms = fieldAtPath(event2.params, "srcImmutables");
  const destination = fieldAtPath(event2.params, "dstImmutablesComplement");
  const created = event2.name === "SrcEscrowCreated" || event2.name === "DstEscrowCreated";
  return /* @__PURE__ */ jsx(SectionCard, { title: hint.label ?? event2.name, protocolId: PID, meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: PID }), children: /* @__PURE__ */ jsxs("div", { className: "space-y-3 min-w-0", "data-testid": "oneinch-escrow-event", children: [
    /* @__PURE__ */ jsx(FactGrid, { facts: [
      fact("Emitted by", event2.address),
      fact("Escrow", fieldAtPath(event2.params, "escrow")),
      fact("Hashlock", fieldAtPath(event2.params, "hashlock")),
      fact("Taker", fieldAtPath(event2.params, "taker"))
    ] }),
    /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: created ? "Escrow creation is a lock step, not proof of delivery. Follow the linked withdrawal or cancellation." : "This event records one escrow operation. The linked journey and transfers establish delivery or refund." }),
    terms != null && /* @__PURE__ */ jsx(EscrowTerms, { value: terms }),
    destination != null && /* @__PURE__ */ jsx(ProtocolDisclosure, { summary: "Destination escrow terms", children: /* @__PURE__ */ jsx(ProtocolValue, { value: destination }) }),
    /* @__PURE__ */ jsx(ProtocolDisclosure, { summary: "All event fields", children: /* @__PURE__ */ jsx(ProtocolParamList, { params: event2.params, emptyLabel: "No event fields" }) })
  ] }) });
}
var tuples = ["(bytes32,bytes32,uint256,uint256,uint256,uint256,uint256,uint256)", "(bytes32,bytes32,uint256,uint256,uint256,uint256,uint256,uint256,bytes)"];
var ONEINCH_ESCROW_HINTS = {
  valueHints: [],
  callHints: tuples.flatMap((tuple) => [
    ["createDstEscrow", `${tuple},uint256`, "Create destination escrow"],
    ["withdraw", `bytes32,${tuple}`, "Withdraw escrow"],
    ["publicWithdraw", `bytes32,${tuple}`, "Public escrow withdrawal"],
    ["withdrawTo", `bytes32,address,${tuple}`, "Withdraw escrow to target"],
    ["cancel", tuple, "Cancel escrow"],
    ["publicCancel", tuple, "Public escrow cancellation"]
  ].map(([call2, args, label]) => ({
    protocolId: PID,
    call: call2,
    signature: `${call2}(${args})`,
    label,
    component: "OneInchEscrowCall",
    summaryFields: []
  }))),
  eventHints: [
    ["DstEscrowCreated", "address,bytes32,uint256", "Destination escrow created"],
    ["SrcEscrowCreated", `${tuples[0]},(uint256,uint256,uint256,uint256,uint256)`, "Source escrow created"],
    ["SrcEscrowCreated", `${tuples[1]},(uint256,uint256,uint256,uint256,uint256,bytes)`, "Source escrow created"],
    ["EscrowWithdrawal", "bytes32", "Escrow withdrawal"],
    ["EscrowCancelled", "", "Escrow cancelled"]
  ].map(([event2, args, label]) => ({
    protocolId: PID,
    event: event2,
    signature: `${event2}(${args})`,
    label,
    component: "OneInchEscrowEvent",
    summaryFields: []
  }))
};
function registerOneInchEscrow(registry) {
  registry.registerCall("OneInchEscrowCall", OneInchEscrowCall);
  registry.registerEvent("OneInchEscrowEvent", OneInchEscrowEvent);
  registry.registerHints(ONEINCH_ESCROW_HINTS);
}

// src/protocols/oneinch/index.ts
var PID2 = ONEINCH.protocolId;
var C = ONEINCH_COMPONENTS;
var value = (kind, component) => ({
  protocolId: PID2,
  kind,
  component,
  fields: []
});
var call = (name, component) => ({
  protocolId: PID2,
  call: name,
  component,
  summaryFields: []
});
var event = (name, component) => ({
  protocolId: PID2,
  event: name,
  component,
  summaryFields: []
});
var ONEINCH_HINTS = {
  valueHints: [
    value(ONEINCH_KINDS.address, C.address),
    value(ONEINCH_KINDS.dex, C.pool),
    value(ONEINCH_KINDS.makerTraits, C.makerTraits),
    value(ONEINCH_KINDS.takerTraits, C.takerTraits),
    value(ONEINCH_KINDS.fillArgs, C.fillArgs),
    value(ONEINCH_KINDS.extension, C.extension),
    value(ONEINCH_KINDS.permit, C.permit)
  ],
  callHints: [
    call(ONEINCH_CALLS.swap, C.swap),
    call(ONEINCH_CALLS.unoswap, C.unoswap),
    call(ONEINCH_CALLS.fillOrder, C.fillOrder),
    ...["fillOrderArgs", "fillContractOrder", "fillContractOrderArgs"].map((name) => call(name, C.fillOrder)),
    ...["getMakingAmount", "getTakingAmount"].map((name) => call(name, C.amountGetter)),
    call(ONEINCH_CALLS.cancelOrder, C.cancelOrder),
    call(ONEINCH_CALLS.uniswapV3Swap, C.legacyRoute),
    call(ONEINCH_CALLS.clipperSwap, C.clipperSwap),
    call(ONEINCH_CALLS.fillOrderRfq, C.rfqFill),
    call(ONEINCH_CALLS.settleOrders, C.fusionSettle)
  ],
  eventHints: [
    event(ONEINCH_EVENTS.orderFilled, C.orderFilled),
    event(ONEINCH_EVENTS.orderCancelled, C.orderCancelled),
    event(ONEINCH_EVENTS.epochIncreased, C.epochIncreased),
    event(ONEINCH_EVENTS.swapped, C.swapped),
    event(ONEINCH_EVENTS.rfqOrderFilled, C.rfqOrderFilled),
    event(ONEINCH_EVENTS.orderCanceled, C.orderCanceled),
    event(ONEINCH_EVENTS.nonceIncreased, C.nonceIncreased)
  ]
};
function registerOneInch(registry) {
  registerOneInchEscrow(registry);
  registry.registerValue(C.address, OneInchAddress);
  registry.registerValue(C.pool, OneInchPool);
  registry.registerValue(C.makerTraits, OneInchTraits);
  registry.registerValue(C.takerTraits, OneInchTraits);
  registry.registerValue(C.fillArgs, OneInchPayload);
  registry.registerValue(C.extension, OneInchPayload);
  registry.registerValue(C.permit, OneInchPayload);
  registry.registerCall(C.swap, GenericCallCard);
  registry.registerCall(C.unoswap, GenericCallCard);
  registry.registerCall(C.fillOrder, OneInchOrderCall);
  registry.registerCall(C.amountGetter, OneInchOrderCall);
  registry.registerCall(C.cancelOrder, GenericCallCard);
  registry.registerCall(C.legacyRoute, GenericCallCard);
  registry.registerCall(C.clipperSwap, GenericCallCard);
  registry.registerCall(C.rfqFill, GenericCallCard);
  registry.registerCall(C.fusionSettle, GenericCallCard);
  registry.registerEvent(C.orderFilled, GenericEventCard);
  registry.registerEvent(C.orderCancelled, GenericEventCard);
  registry.registerEvent(C.epochIncreased, GenericEventCard);
  registry.registerEvent(C.swapped, GenericEventCard);
  registry.registerEvent(C.rfqOrderFilled, GenericEventCard);
  registry.registerEvent(C.orderCanceled, GenericEventCard);
  registry.registerEvent(C.nonceIncreased, GenericEventCard);
  registry.registerHints(ONEINCH_HINTS);
  return registry;
}

export { ONEINCH_ESCROW_HINTS, ONEINCH_HINTS, OneInchAddress, OneInchEscrowCall, OneInchEscrowEvent, OneInchOrderCall, OneInchPayload, OneInchPool, OneInchTraits, registerOneInch };
//# sourceMappingURL=chunk-YDDPYKRH.js.map
//# sourceMappingURL=chunk-YDDPYKRH.js.map